// Page principale de l'Agent IA Vendeur
// Dashboard avec stats, accès rapide au catalogue et conversations

import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Bot, MessageSquare, Users, Package, Settings, 
  TrendingUp, CheckCircle, Clock, ArrowRight,
  Plus, Zap, AlertCircle
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../../components/ui/card';
import { toast } from 'sonner';

import { API_URL } from '../../../config/api';

export default function AIVendorDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [config, setConfig] = useState(null);
  const [recentConversations, setRecentConversations] = useState([]);
  const [loading, setLoading] = useState(true);

  const token = localStorage.getItem('clientToken');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      // Fetch stats
      const statsRes = await fetch(`${API_URL}/api/vendor/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (statsRes.ok) {
        setStats(await statsRes.json());
      }

      // Fetch config
      const configRes = await fetch(`${API_URL}/api/vendor/config`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (configRes.ok) {
        setConfig(await configRes.json());
      }

      // Fetch recent conversations
      const convRes = await fetch(`${API_URL}/api/vendor/conversations?limit=5`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (convRes.ok) {
        setRecentConversations(await convRes.json());
      }
    } catch (e) {
      console.error('Error fetching data:', e);
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  const toggleAgent = async () => {
    try {
      const res = await fetch(`${API_URL}/api/vendor/config`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ is_active: !config?.is_active })
      });
      
      if (res.ok) {
        const updated = await res.json();
        setConfig(updated);
        toast.success(updated.is_active ? 'Agent activé' : 'Agent désactivé');
      }
    } catch (e) {
      toast.error('Erreur');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="ai-vendor-dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            <Bot className="text-blue-600" size={28} />
            Agent IA Vendeur
          </h1>
          <p className="text-gray-600 mt-1">
            Votre assistant commercial automatisé 24h/24
          </p>
        </div>
        
        <div className="flex items-center gap-3">
          <Button
            variant={config?.is_active ? "default" : "outline"}
            onClick={toggleAgent}
            className={config?.is_active ? "bg-green-600 hover:bg-green-700" : ""}
          >
            {config?.is_active ? (
              <>
                <Zap size={18} className="mr-2" />
                Agent Actif
              </>
            ) : (
              <>
                <AlertCircle size={18} className="mr-2" />
                Agent Inactif
              </>
            )}
          </Button>
          
          <Link to="/espace-client/ai-vendor/settings">
            <Button variant="outline">
              <Settings size={18} className="mr-2" />
              Paramètres
            </Button>
          </Link>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Conversations</p>
                <p className="text-2xl font-bold text-gray-900">
                  {stats?.total_conversations || 0}
                </p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                <MessageSquare className="text-blue-600" size={24} />
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              {stats?.active_conversations || 0} actives
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Leads Qualifiés</p>
                <p className="text-2xl font-bold text-gray-900">
                  {stats?.qualified_leads || 0}
                </p>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                <Users className="text-green-600" size={24} />
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              sur {stats?.total_leads || 0} total
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Taux de Conversion</p>
                <p className="text-2xl font-bold text-gray-900">
                  {stats?.conversion_rate || 0}%
                </p>
              </div>
              <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
                <TrendingUp className="text-orange-600" size={24} />
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              {stats?.converted_leads || 0} convertis
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Catalogue</p>
                <p className="text-2xl font-bold text-gray-900">
                  {stats?.catalog_items || 0}
                </p>
              </div>
              <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                <Package className="text-purple-600" size={24} />
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              produits/services
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Conversations */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center justify-between">
              <span className="flex items-center gap-2">
                <MessageSquare size={20} />
                Conversations Récentes
              </span>
              <Link to="/espace-client/ai-vendor/conversations">
                <Button variant="ghost" size="sm">
                  Voir tout <ArrowRight size={16} className="ml-1" />
                </Button>
              </Link>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {recentConversations.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <MessageSquare size={32} className="mx-auto mb-2 opacity-50" />
                <p>Aucune conversation</p>
                <p className="text-sm">Les visiteurs pourront discuter avec ton agent</p>
              </div>
            ) : (
              <div className="space-y-3">
                {recentConversations.map((conv) => (
                  <Link 
                    key={conv.id}
                    to={`/espace-client/ai-vendor/conversations/${conv.id}`}
                    className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
                      {conv.visitor_name?.[0]?.toUpperCase() || '?'}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900 truncate">
                        {conv.visitor_name || 'Visiteur anonyme'}
                      </p>
                      <p className="text-sm text-gray-500 truncate">
                        {conv.main_request || 'Nouvelle conversation'}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      {conv.is_human_takeover && (
                        <span className="px-2 py-1 bg-yellow-100 text-yellow-700 text-xs rounded-full">
                          En cours
                        </span>
                      )}
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        conv.lead_status === 'qualified' ? 'bg-green-100 text-green-700' :
                        conv.lead_status === 'new' ? 'bg-blue-100 text-blue-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                        {conv.lead_status === 'qualified' ? 'Qualifié' :
                         conv.lead_status === 'new' ? 'Nouveau' : conv.lead_status}
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Quick Setup */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Zap size={20} />
              Configuration Rapide
            </CardTitle>
            <CardDescription>
              Personnalise ton agent en quelques clics
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Catalog Setup */}
            <Link 
              to="/espace-client/ai-vendor/catalog"
              className="flex items-center gap-4 p-4 rounded-xl border border-gray-200 hover:border-blue-300 hover:bg-blue-50/50 transition-colors"
            >
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                stats?.catalog_items > 0 ? 'bg-green-100' : 'bg-gray-100'
              }`}>
                {stats?.catalog_items > 0 ? (
                  <CheckCircle className="text-green-600" size={24} />
                ) : (
                  <Package className="text-gray-400" size={24} />
                )}
              </div>
              <div className="flex-1">
                <p className="font-medium text-gray-900">Catalogue Produits/Services</p>
                <p className="text-sm text-gray-500">
                  {stats?.catalog_items > 0 
                    ? `${stats.catalog_items} éléments configurés`
                    : 'Ajoute tes prestations et produits'}
                </p>
              </div>
              <ArrowRight className="text-gray-400" size={20} />
            </Link>

            {/* Agent Settings */}
            <Link 
              to="/espace-client/ai-vendor/settings"
              className="flex items-center gap-4 p-4 rounded-xl border border-gray-200 hover:border-blue-300 hover:bg-blue-50/50 transition-colors"
            >
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                config?.custom_greeting ? 'bg-green-100' : 'bg-gray-100'
              }`}>
                {config?.custom_greeting ? (
                  <CheckCircle className="text-green-600" size={24} />
                ) : (
                  <Bot className="text-gray-400" size={24} />
                )}
              </div>
              <div className="flex-1">
                <p className="font-medium text-gray-900">Personnalité de l'Agent</p>
                <p className="text-sm text-gray-500">
                  {config?.agent_name || 'Assistant'} - {config?.tone || 'Professional'}
                </p>
              </div>
              <ArrowRight className="text-gray-400" size={20} />
            </Link>

            {/* Leads */}
            <Link 
              to="/espace-client/ai-vendor/leads"
              className="flex items-center gap-4 p-4 rounded-xl border border-gray-200 hover:border-blue-300 hover:bg-blue-50/50 transition-colors"
            >
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                stats?.total_leads > 0 ? 'bg-green-100' : 'bg-gray-100'
              }`}>
                {stats?.total_leads > 0 ? (
                  <Users className="text-green-600" size={24} />
                ) : (
                  <Clock className="text-gray-400" size={24} />
                )}
              </div>
              <div className="flex-1">
                <p className="font-medium text-gray-900">Leads à Traiter</p>
                <p className="text-sm text-gray-500">
                  {stats?.qualified_leads > 0 
                    ? `${stats.qualified_leads} leads qualifiés en attente`
                    : 'Les leads apparaîtront ici'}
                </p>
              </div>
              <ArrowRight className="text-gray-400" size={20} />
            </Link>
          </CardContent>
        </Card>
      </div>

      {/* Agent Preview */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Aperçu de ton Agent</CardTitle>
          <CardDescription>
            Voici comment les visiteurs verront ton agent sur ta boutique
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="bg-gray-100 rounded-xl p-6">
            <div className="max-w-md mx-auto bg-white rounded-2xl shadow-lg overflow-hidden">
              {/* Chat Header */}
              <div className="bg-blue-600 text-white p-4 flex items-center gap-3">
                <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                  <Bot size={20} />
                </div>
                <div>
                  <p className="font-semibold">{config?.agent_name || 'Assistant'}</p>
                  <p className="text-sm text-blue-100">En ligne</p>
                </div>
              </div>
              
              {/* Chat Messages */}
              <div className="p-4 space-y-3">
                <div className="flex gap-2">
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <Bot size={16} className="text-blue-600" />
                  </div>
                  <div className="bg-gray-100 rounded-2xl rounded-tl-md px-4 py-2 max-w-[80%]">
                    <p className="text-gray-800 text-sm">
                      {config?.custom_greeting || `Bonjour ! Je suis ${config?.agent_name || 'l\'assistant'}. Comment puis-je t'aider ?`}
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Chat Input */}
              <div className="border-t p-3">
                <div className="flex items-center gap-2">
                  <input 
                    type="text" 
                    placeholder="Écris ton message..."
                    className="flex-1 px-4 py-2 bg-gray-100 rounded-full text-sm"
                    disabled
                  />
                  <button className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center text-white">
                    <ArrowRight size={18} />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
